import React from 'react';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Alert from '@amzn/meridian/alert';
import { GetContainerDetailsOutput } from 'src/models/GetContainerDetails';
import * as AppStrings from 'src/localization/string-ids/app-strings';
import { getContainerIdString, getThumbnailFromContainerType } from 'src/data-map/containerDetailsToStringID';
import { ContainerDetailsModel, ContainerType } from 'src/components/common/container-details/ContainerDetailsModel';
import { SearchPages } from 'src/store/common-slices/searchSlice';
import Text from '@amzn/meridian/text';
import Link from '@amzn/meridian/link';
import {
    getChildrenType,
    getChildrenCount,
    getGrandChildrenCount,
    getGrandChildrenType,
    containsInventoryItem
} from 'src/utils/container-details-util';
import 'src/components/pages/search-page/Search.scss';

interface SearchedContainerDetailsForScannerStateProps {
    containerDetails?: GetContainerDetailsOutput;
}

interface SearchedContainerDetailsForScannerDispatchProps {
    setPageToRender: (pageToRender: SearchPages) => void;
}

type Props = SearchedContainerDetailsForScannerStateProps & SearchedContainerDetailsForScannerDispatchProps;

class SearchedContainerDetailsTableForScanners extends React.Component<Props, any> {

    private properties = [
        { key: 'sortationCategories', label: AppStrings.SEARCH_TABLE_SORTATION_CATEGORY },
        { key: 'containerProperties.cleanDecantDate', label: AppStrings.SEARCH_TABLE_CDD },
        { key: 'containerProperties.latestProcessingDate', label: AppStrings.SEARCH_TABLE_LPD },
        { key: 'containerProperties.RMAId', label: AppStrings.SEARCH_TABLE_RMA_ID },
        { key: 'containerProperties.Asin', label: AppStrings.SEARCH_TABLE_ASIN },
        { key: 'containerProperties.cleanDecantSortRuleId', label: AppStrings.SEARCH_TABLE_RULE_ID },
        { key: 'containerProperties.SellerType', label: AppStrings.SEARCH_TABLE_SELLER_TYPE },
        { key: 'containerProperties.TTSorTTA', label: AppStrings.SEARCH_TABLE_TTS_OR_TTA },
        { key: 'containerProperties.requiredHazmatLabels', label: AppStrings.SEARCH_TABLE_HAZMAT_LABELS },
        { key: 'latestContainerOperation.associate', label: AppStrings.SEARCH_TABLE_CLEAN_DECANT_AA }
    ];

    /*
        This function is to return the rows against different properties of the container like cleanDecantDate, etc.
        For every property from the properties array, if there's a value in the container details then it renders a row against it.
    */
    private getPropertiesRows = (container: ContainerDetailsModel) => {
        return this.properties.map(({key, label}) => {
            const value = key.split('.').reduce((acc:any, curr) => acc?.[curr], container);
            if (value) {
                return (
                    <TableRow>
                        <TableCell>{label}</TableCell>
                        <TableCell header={true}>{value}</TableCell>
                    </TableRow>
                )
            }
            return undefined;
        })
    }

    /*
        This is to render the containerId row.
        For Example, if we searched for some pallet - it will render a row:
        Pallet ID: paXxxx
    */
    private getContainerIdRow = (container: ContainerDetailsModel) => {
        if (container?.containerType) {
            return (
                <TableRow>
                    <TableCell>
                        <div className="thumbnail-div">
                            <img className="container-thumbnail" alt={""}
                                 src={getThumbnailFromContainerType(container.containerType)}/>
                            <Text>{getContainerIdString(this.props.containerDetails?.container?.containerType)}</Text>
                        </div>
                    </TableCell>
                    <TableCell header={true}>{this.props.containerDetails?.container?.containerId}</TableCell>
                </TableRow>
            );
        }
    }

    /*
        This is to render a row for number of child containers.
        For example, if we searched for a PALLET with 5 Totes inside it, then this will render a row:
        TOTE: 5: View TOTE
    */
    private getNumOfChildContainerRow = () => {
        const childrenType: ContainerType|undefined = getChildrenType(this.props.containerDetails?.container);
        const childrenCount: number|undefined = getChildrenCount(this.props.containerDetails?.container);
        if (childrenType) {
            return (
                <TableRow>
                    <TableCell>{childrenType}</TableCell>
                    <TableCell header={true}>
                        {childrenCount}:
                        <Link onClick={this.onViewItems}>{AppStrings.SEARCH_TABLE_VIEW_ITEMS} {childrenType}</Link>
                    </TableCell>
                </TableRow>
            );
        }
    }

    /*
        This is to render a row for number of Grand child containers.
        For example, if we searched for a PALLET which contains 5 Totes and each tote contains 10 Items,
        Then that would mean there are 50 Items inside the pallet. This will render a row:
        ITEM: 50
    */
    private getNumOfGrandChildContainerRow = () => {
        const grandChildrenType: ContainerType|undefined = getGrandChildrenType(this.props.containerDetails);
        const grandChildrenCount: number|undefined = getGrandChildrenCount(this.props.containerDetails);
        if (grandChildrenType) {
            return (
                <TableRow>
                    <TableCell>{grandChildrenType}</TableCell>
                    <TableCell header={true}>
                        {grandChildrenCount}
                    </TableCell>
                </TableRow>
            )
        }
    }

    /* When user Clicks on "View ITEM" / View "TOTE" in childContainer row,
       Then it should open the page with details of those child containers. */
    private onViewItems = async () => {
        await this.props.setPageToRender(SearchPages.CHILD_CONTAINER);
    }

    /*
        Renders a warning alert when the tote contains at least one inventory item.
        Only applicable for TOTE containers with child items that have inventoryCreationStatus = InventoryCreated.
    */
    private getContainsInventoryItemAlert = () => {
        if (containsInventoryItem(this.props.containerDetails)) {
            return (
                <TableRow>
                    <TableCell>
                        <Alert type="warning" size="small">
                            {AppStrings.CONTAINS_INVENTORY_ITEM}
                        </Alert>
                    </TableCell>
                </TableRow>
            );
        }
        return null;
    }

    public render() {
        return(
            <Table showDividers={true} className={"scanner-screen-table"} data-testid="search-results-table-scanner">
                {this.props.containerDetails && this.getContainsInventoryItemAlert()}
                {this.props.containerDetails && this.getContainerIdRow(this.props.containerDetails.container)}
                {this.props.containerDetails && this.getNumOfChildContainerRow()}
                {this.props.containerDetails && this.getNumOfGrandChildContainerRow()}
                {this.props.containerDetails?.container && this.getPropertiesRows(this.props.containerDetails?.container)}
            </Table>
        );
    }
}

export default SearchedContainerDetailsTableForScanners;
